/**
 * Hook para gerenciamento de webhooks de workspace
 * 
 * Fornece CRUD completo para configuração de endpoints webhook
 * que permitem sistemas externos criar negócios automaticamente.
 * 
 * @release 2026-01-20
 * @version 1.0.0
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { getEdgeFunctionUrl } from '@/lib/supabaseHelpers';

// Logger para o hook
const logger = createLogger('useWorkspaceWebhooks');

// Tipagem do webhook
export interface WorkspaceWebhook {
  id: string;
  workspace_id: string;
  webhook_token: string;
  name: string;
  description: string | null;
  default_pipeline_id: string | null;
  default_phase_id: string | null;
  default_source: string;
  default_seller_id: string | null;
  field_mapping: Record<string, string>;
  default_products: Array<{
    product_id: string;
    quantity: number;
    unit_price?: number;
  }>;
  is_active: boolean;
  require_auth: boolean;
  auth_header_name: string;
  auth_header_value: string;
  check_duplicate: boolean;
  duplicate_scope: string;
  duplicate_window_value: number;
  duplicate_window_unit: string;
  duplicate_behavior: string;
  total_requests: number;
  successful_deals: number;
  failed_requests: number;
  last_request_at: string | null;
  created_at: string;
  updated_at: string;
}

// Tipagem do log de webhook
export interface WebhookLog {
  id: string;
  webhook_id: string;
  request_payload: Record<string, unknown>;
  request_ip: string;
  status: 'success' | 'error' | 'duplicate' | 'validation_error' | 'auth_error';
  deal_id: string | null;
  contact_id: string | null;
  error_message: string | null;
  error_code: string | null;
  processing_time_ms: number;
  created_at: string;
  total_count?: number; // Novo: para paginação server-side
}

// Tipagem para volume por hora do webhook
export interface WebhookVolumeData {
  hora: number;
  data_hora: string;
  total: number;
  sucesso: number;
  erro: number;
}

// Interface para resultado paginado de logs
export interface PaginatedWebhookLogsResult {
  logs: WebhookLog[];
  totalCount: number;
}

// Parâmetros para criação de webhook
export interface CreateWebhookParams {
  workspace_id: string;
  name: string;
  description?: string;
  default_pipeline_id?: string;
  default_phase_id?: string;
  default_source?: string;
  default_seller_id?: string;
  field_mapping?: Record<string, string>;
  default_products?: Array<{
    product_id: string;
    quantity: number;
    unit_price?: number;
  }>;
  require_auth?: boolean;
  auth_header_value?: string;
  check_duplicate?: boolean;
  duplicate_scope?: string;
  duplicate_window_value?: number;
  duplicate_window_unit?: string;
  duplicate_behavior?: string;
}

// Parâmetros para atualização de webhook
export interface UpdateWebhookParams {
  webhook_id: string;
  name?: string;
  description?: string;
  default_pipeline_id?: string;
  default_phase_id?: string;
  default_source?: string;
  default_seller_id?: string;
  field_mapping?: Record<string, string>;
  default_products?: Array<{
    product_id: string;
    quantity: number;
    unit_price?: number;
  }>;
  is_active?: boolean;
  require_auth?: boolean;
  auth_header_value?: string;
  check_duplicate?: boolean;
  duplicate_scope?: string;
  duplicate_window_value?: number;
  duplicate_window_unit?: string;
  duplicate_behavior?: string;
}

/**
 * Hook principal para gerenciamento de webhooks de workspace
 */
export function useWorkspaceWebhooks(workspaceId?: string) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  
  // Query para buscar webhooks de um workspace
  const {
    data: webhooks,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['workspace-webhooks', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      
      logger.debug('Buscando webhooks do workspace', workspaceId);
      
      const { data, error } = await supabase.rpc('get_workspace_webhooks', {
        p_workspace_id: workspaceId,
      });
      
      if (error) {
        logger.error('Erro ao buscar webhooks', error.message);
        throw error;
      }
      
      return (data || []) as WorkspaceWebhook[];
    },
    enabled: !!workspaceId && !!currentTenant?.id,
  });
  
  // Mutation para criar webhook
  const createWebhookMutation = useMutation({
    mutationFn: async (params: CreateWebhookParams) => {
      logger.info('Criando webhook', params.name);
      
      const { data, error } = await supabase.rpc('create_workspace_webhook', {
        p_workspace_id: params.workspace_id,
        p_name: params.name,
        p_description: params.description || null,
        p_default_pipeline_id: params.default_pipeline_id || null,
        p_default_phase_id: params.default_phase_id || null,
        p_default_source: params.default_source || 'webhook',
        p_default_seller_id: params.default_seller_id || null,
        p_field_mapping: params.field_mapping || null,
        p_default_products: params.default_products || [],
        p_require_auth: params.require_auth || false,
        p_auth_header_value: params.auth_header_value || null,
        p_check_duplicate: params.check_duplicate || false,
        p_duplicate_scope: params.duplicate_scope || 'pipeline',
        p_duplicate_window_value: params.duplicate_window_value || 7,
        p_duplicate_window_unit: params.duplicate_window_unit || 'days',
        p_duplicate_behavior: params.duplicate_behavior || 'skip_if_open',
      });
      
      if (error) {
        logger.error('Erro ao criar webhook', error.message);
        throw error;
      }
      
      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-webhooks', workspaceId] });
      toast.success('Webhook criado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar webhook: ${error.message}`);
    },
  });
  
  // Mutation para atualizar webhook
  const updateWebhookMutation = useMutation({
    mutationFn: async (params: UpdateWebhookParams) => {
      logger.info('Atualizando webhook', params.webhook_id);
      
      const { data, error } = await supabase.rpc('update_workspace_webhook', {
        p_webhook_id: params.webhook_id,
        p_name: params.name || null,
        p_description: params.description || null,
        p_default_pipeline_id: params.default_pipeline_id || null,
        p_default_phase_id: params.default_phase_id || null,
        p_default_source: params.default_source || null,
        p_default_seller_id: params.default_seller_id || null,
        p_field_mapping: params.field_mapping || null,
        p_default_products: params.default_products || null,
        p_is_active: params.is_active ?? null,
        p_require_auth: params.require_auth ?? null,
        p_auth_header_value: params.auth_header_value || null,
        p_check_duplicate: params.check_duplicate ?? null,
        p_duplicate_scope: params.duplicate_scope || null,
        p_duplicate_window_value: params.duplicate_window_value ?? null,
        p_duplicate_window_unit: params.duplicate_window_unit || null,
        p_duplicate_behavior: params.duplicate_behavior || null,
      });
      
      if (error) {
        logger.error('Erro ao atualizar webhook', error.message);
        throw error;
      }
      
      return data as boolean;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-webhooks', workspaceId] });
      toast.success('Webhook atualizado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar webhook: ${error.message}`);
    },
  });
  
  // Mutation para deletar webhook
  const deleteWebhookMutation = useMutation({
    mutationFn: async (webhookId: string) => {
      logger.info('Deletando webhook', webhookId);
      
      const { data, error } = await supabase.rpc('delete_workspace_webhook', {
        p_webhook_id: webhookId,
      });
      
      if (error) {
        logger.error('Erro ao deletar webhook', error.message);
        throw error;
      }
      
      return data as boolean;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-webhooks', workspaceId] });
      toast.success('Webhook removido com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao remover webhook: ${error.message}`);
    },
  });
  
  // Mutation para regenerar token
  const regenerateTokenMutation = useMutation({
    mutationFn: async (webhookId: string) => {
      logger.info('Regenerando token', webhookId);
      
      const { data, error } = await supabase.rpc('regenerate_webhook_token', {
        p_webhook_id: webhookId,
      });
      
      if (error) {
        logger.error('Erro ao regenerar token', error.message);
        throw error;
      }
      
      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-webhooks', workspaceId] });
      toast.success('Token regenerado! O token anterior foi invalidado.');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao regenerar token: ${error.message}`);
    },
  });
  
  // Função para buscar logs de um webhook (retrocompatibilidade)
  const fetchWebhookLogs = async (
    webhookId: string,
    limit = 50,
    offset = 0,
    status?: string
  ): Promise<WebhookLog[]> => {
    logger.debug('Buscando logs', webhookId);
    
    const { data, error } = await supabase.rpc('get_webhook_logs', {
      p_webhook_id: webhookId,
      p_limit: limit,
      p_offset: offset,
      p_status: status || null,
    });
    
    if (error) {
      logger.error('Erro ao buscar logs', error.message);
      throw error;
    }
    
    // Remove total_count para manter retrocompatibilidade
    const rawLogs = (data || []) as WebhookLog[];
    return rawLogs.map(({ total_count, ...log }) => log as WebhookLog);
  };

  /**
   * Busca logs paginados do webhook (server-side)
   * Reutiliza get_webhook_logs que agora retorna total_count
   * @param webhookId - ID do webhook
   * @param page - Página atual (1-indexed)
   * @param pageSize - Itens por página
   * @param status - Filtro de status opcional
   * @returns Logs paginados com totalCount
   */
  const fetchWebhookLogsPaginated = async (
    webhookId: string,
    page: number = 1,
    pageSize: number = 10,
    status?: string
  ): Promise<PaginatedWebhookLogsResult> => {
    const offset = (page - 1) * pageSize;
    
    logger.debug('Buscando logs paginados', { webhookId, page, pageSize, offset });
    
    const { data, error } = await supabase.rpc('get_webhook_logs', {
      p_webhook_id: webhookId,
      p_limit: pageSize,
      p_offset: offset,
      p_status: status || null,
    });
    
    if (error) {
      logger.error('Erro ao buscar logs paginados', error.message);
      throw error;
    }
    
    const rawLogs = (data || []) as WebhookLog[];
    // Extrai total_count do primeiro registro (padrão COUNT(*) OVER())
    const totalCount = rawLogs[0]?.total_count ?? 0;
    
    // Remove total_count dos objetos para não poluir
    const logs = rawLogs.map(({ total_count, ...log }) => log as WebhookLog);
    
    return { logs, totalCount: Number(totalCount) };
  };

  /**
   * Busca volume de requisições por hora para gráfico
   * @param webhookId - ID do webhook
   * @param hours - Quantidade de horas (padrão: 24)
   * @returns Dados de volume agregados por hora
   */
  const fetchWebhookVolume = async (
    webhookId: string,
    hours: number = 24
  ): Promise<WebhookVolumeData[]> => {
    logger.debug('Buscando volume por hora', { webhookId, hours });
    
    const { data, error } = await supabase.rpc('get_webhook_volume_by_hour', {
      p_webhook_id: webhookId,
      p_hours: hours,
    });
    
    if (error) {
      logger.error('Erro ao buscar volume por hora', error.message);
      throw error;
    }
    
    return (data || []) as WebhookVolumeData[];
  };
  
  // Gera URL completa do webhook usando helper centralizado (sem hardcode)
  const getWebhookUrl = (token: string): string => {
    const baseUrl = getEdgeFunctionUrl('incoming-deal-webhook');
    return `${baseUrl}?token=${token}`;
  };
  
  return {
    // Dados
    webhooks: webhooks || [],
    isLoading,
    error,
    
    // Ações
    refetch,
    createWebhook: createWebhookMutation.mutateAsync,
    updateWebhook: updateWebhookMutation.mutateAsync,
    deleteWebhook: deleteWebhookMutation.mutateAsync,
    regenerateToken: regenerateTokenMutation.mutateAsync,
    fetchWebhookLogs,
    fetchWebhookLogsPaginated, // Nova função para paginação server-side
    fetchWebhookVolume, // Nova função para gráfico de volume
    getWebhookUrl,
    
    // Estados das mutations
    isCreating: createWebhookMutation.isPending,
    isUpdating: updateWebhookMutation.isPending,
    isDeleting: deleteWebhookMutation.isPending,
    isRegeneratingToken: regenerateTokenMutation.isPending,
  };
}
